import { userTagCanAssign, type UserTag } from '@/store/userTag';
import type { AccountGroupTagOption } from '@/utils/accountGroupTagAssignments';

export type AccountTagOption = AccountGroupTagOption;

/**
 * Builds picker options from GET /api/user/tags.
 * Only tags the user can assign (Assign or Edit) are included. The first row
 * for an id wins. Names are trimmed and empty names are dropped.
 */
export function buildAccountTagOptions(
  tags: readonly UserTag[],
): AccountTagOption[] {
  const seen = new Set<number>();
  const list: AccountTagOption[] = [];

  for (const tag of tags) {
    const name = tag.name.trim();
    if (
      !userTagCanAssign(tag) ||
      !Number.isFinite(tag.tagId) ||
      name.length === 0 ||
      seen.has(tag.tagId)
    ) {
      continue;
    }
    seen.add(tag.tagId);
    list.push({
      value: tag.tagId,
      label: name,
    });
  }

  return list.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Ensures the current TagSelectorDropdown tag is present in picker options so
 * a prefilled chip can render even if the catalog has not listed it yet.
 */
export function mergeSelectedViewingTagOption(
  options: AccountTagOption[],
  tag: { tagId: number; name: string } | null,
): AccountTagOption[] {
  if (tag == null || options.some((option) => option.value === tag.tagId)) {
    return options;
  }
  return [...options, { value: tag.tagId, label: tag.name }].sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}
